import React, { useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { Icon as ScaleUIIcon } from '@scale/scaleui';
import { TimePicker } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/TimePicker';
import { SessionTime, RepeatType } from 'react-scale/components/WebinarSessions/types';
import { RecurringType } from '@scale/shared/webinarSessions/types';
import {
  calculateFutureOccurrencesV2,
  timeDifferenceInMinutes,
  getNewStartDateTime,
  getStartTimeFromDate,
  getEndTimeFromStartTime,
} from 'react-scale/components/WebinarSessions/hooks/calculateFutureOccurrences';
import {
  DatePicker,
  FormField,
  Flex,
  TextField,
  Select,
  Button,
  Icon,
  Text,
  Box,
  IconButton,
  Card,
} from '@scale/scaleui-radix';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
  WebinarError,
  WebinarErrorMessages,
} from 'react-scale/components/WebinarSessions/constants';
import { ErrorMessage } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ErrorMessage';
import { UserAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserAutocomplete';
import { useUser } from 'react-scale/components/ui/UserContext';
const DAYS_OF_THE_WEEK = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

interface SchedulerProps {
  sessionType: RecurringType;
  setDuration: (duration: number | null) => void;
  eventStart: Date | null;
  setEventStart: (eventStart: Date | null) => void;
  numOccurrences: number | null;
  setNumOccurrences: (numOccurrences: number | null) => void;
  selectedDate: Date;
  setSelectedDate: (selectedDate: Date) => void;
  startTime: string;
  setStartTime: (startTime: string) => void;
  endTime: string;
  setEndTime: (endTime: string) => void;
  hostedBy: string[];
  setHostedBy: (hostedBy: string[]) => void;
  repeatNumber: number | null;
  setRepeatNumber: (repeatNumber: number | null) => void;
  repeatType: RepeatType;
  setRepeatType: (repeatType: RepeatType) => void;
  daysOfTheWeek: string[];
  setDaysOfTheWeek: (daysOfTheWeek: string[]) => void;
  additionalSessions: SessionTime[];
  setAdditionalSessions: (additionalSessions: SessionTime[]) => void;
  occurencesV2: SessionTime[];
  setOccurencesV2: (occurencesV2: SessionTime[]) => void;
  additionalTimesOfDayV2: SessionTime[];
  setAdditionalTimesOfDayV2: (additionalTimesOfDayV2: SessionTime[]) => void;
  getErrors: () => WebinarError[];
  getRecurringErrors: () => Record<number, WebinarError>;
  getAdditionalSessionsErrors: () => Record<number, WebinarError>;
}

// Heavily based on components/WebinarSessions/components/Scheduler.tsx
export const Scheduler = ({
  sessionType,
  setDuration,
  eventStart,
  setEventStart,
  numOccurrences,
  setNumOccurrences,
  selectedDate,
  setSelectedDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  hostedBy,
  setHostedBy,
  repeatNumber,
  setRepeatNumber,
  repeatType,
  setRepeatType,
  daysOfTheWeek,
  setDaysOfTheWeek,
  additionalSessions,
  setAdditionalSessions,
  occurencesV2,
  setOccurencesV2,
  additionalTimesOfDayV2,
  setAdditionalTimesOfDayV2,
  getErrors,
  getRecurringErrors,
  getAdditionalSessionsErrors,
}: SchedulerProps) => {
  const user = useUser();
  const webinarErrors = getErrors();
  const recurringErrors = getRecurringErrors();
  const additionalSessionsErrors = getAdditionalSessionsErrors();

  const handleDaysOfTheWeekClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, day: string) => {
      if (daysOfTheWeek.includes(day)) setDaysOfTheWeek(daysOfTheWeek.filter(d => d !== day));
      else setDaysOfTheWeek([...daysOfTheWeek, day]);
      e.currentTarget.blur();
    },
    [daysOfTheWeek, setDaysOfTheWeek],
  );
  const handleAddTimeOfDay = useCallback(() => {
    const newNumOccurrences =
      _.isNil(numOccurrences) || numOccurrences <= additionalTimesOfDayV2.length + 1
        ? additionalTimesOfDayV2.length + 2
        : numOccurrences;
    setNumOccurrences(newNumOccurrences);

    const date = new Date();
    const startTime = getStartTimeFromDate(date);
    const endTime = getEndTimeFromStartTime(startTime);
    setAdditionalTimesOfDayV2([
      ...additionalTimesOfDayV2,
      {
        date,
        startTime,
        endTime,
        hostedBy: user ? [user.id] : [],
      },
    ]);
  }, [numOccurrences, setNumOccurrences, additionalTimesOfDayV2, setAdditionalTimesOfDayV2, user]);
  const handleRemoveTimeOfDay = useCallback(
    () =>
      setAdditionalTimesOfDayV2([
        ...additionalTimesOfDayV2.slice(0, additionalTimesOfDayV2.length - 1),
      ]),
    [additionalTimesOfDayV2, setAdditionalTimesOfDayV2],
  );

  const futureOccurrences = useMemo(
    () =>
      calculateFutureOccurrencesV2({
        eventStart,
        startTime,
        endTime,
        hostedBy,
        recurring: RecurringType.RECURRING,
        numOccurrences,
        repeatNumber,
        repeatType,
        daysOfTheWeek,
        additionalTimesOfDay: additionalTimesOfDayV2,
      }),
    [
      eventStart,
      startTime,
      endTime,
      hostedBy,
      numOccurrences,
      repeatNumber,
      repeatType,
      daysOfTheWeek,
      additionalTimesOfDayV2,
    ],
  );

  useEffect(() => {
    setEventStart(getNewStartDateTime(selectedDate, startTime));
  }, [setEventStart, selectedDate, startTime]);

  useEffect(() => {
    setOccurencesV2(futureOccurrences);
  }, [futureOccurrences, setOccurencesV2]);

  useEffect(() => {
    const d = timeDifferenceInMinutes(endTime, startTime);
    setDuration(d);
  }, [setDuration, startTime, endTime]);

  return (
    <Flex direction="column" gap="1">
      <Text as="label" size="2" weight="medium">
        {sessionType === RecurringType.SINGLE ? 'Session' : 'Sessions'}
      </Text>

      <Flex direction="column" gap="2">
        <Card>
          <Flex direction="column" gap="2">
            <Flex gap="2" align="center">
              <FormField.Root size="2">
                <FormField.Label htmlFor="webinar-datepicker-label" size="1">
                  Date
                </FormField.Label>
                <DatePicker.Root
                  config={{
                    dates: {
                      mode: 'single',
                      minDate: new Date(),
                    },
                    calendar: {
                      mode: 'fluid',
                    },
                  }}
                  onValueChange={(dates: Date[]) => setSelectedDate(dates[0])}
                  value={[selectedDate]}
                >
                  <DatePicker.Trigger>
                    <DatePicker.TextField />
                  </DatePicker.Trigger>
                  <DatePicker.Content>
                    <DatePicker.Header>
                      <DatePicker.Month />
                      <DatePicker.Year />
                    </DatePicker.Header>
                    <DatePicker.Calendar>
                      <DatePicker.DaysLabels>
                        <DatePicker.DayLabel />
                      </DatePicker.DaysLabels>
                      <DatePicker.Days>
                        <DatePicker.Day />
                      </DatePicker.Days>
                    </DatePicker.Calendar>
                    <DatePicker.Actions>
                      <DatePicker.ActionReset />
                      <DatePicker.ActionApply />
                    </DatePicker.Actions>
                  </DatePicker.Content>
                </DatePicker.Root>
              </FormField.Root>

              <FormField.Root size="2">
                <FormField.Label htmlFor="webinar-start-time-label" size="1">
                  Start time
                </FormField.Label>
                <TimePicker time={startTime} setTime={setStartTime} />
              </FormField.Root>

              <FormField.Root size="2">
                <FormField.Label htmlFor="webinar-end-time-label" size="1">
                  End time
                </FormField.Label>
                <TimePicker time={endTime} setTime={setEndTime} />
              </FormField.Root>
            </Flex>

            <Flex direction="column" gap="2">
              <Text as="label" size="1" weight="medium">
                Owners
              </Text>
              <UserAutocomplete
                userIds={hostedBy}
                onUserIdsChange={setHostedBy}
                limitToScaliens={false}
                limitToContributors={true}
              />
            </Flex>

            {(webinarErrors.includes(WebinarError.START_TIME_IN_PAST) ||
              webinarErrors.includes(WebinarError.END_TIME_BEFORE_START_TIME)) && (
              <ErrorMessage
                text={
                  webinarErrors.includes(WebinarError.START_TIME_IN_PAST)
                    ? WebinarErrorMessages[WebinarError.START_TIME_IN_PAST]
                    : WebinarErrorMessages[WebinarError.END_TIME_BEFORE_START_TIME]
                }
              />
            )}
          </Flex>
        </Card>

        {sessionType === RecurringType.MULTI &&
          additionalSessions.map((session: SessionTime, i: number) => (
            <Card key={`${i}-${session.date.toString()}-${session.startTime}-${session.endTime}`}>
              <Flex direction="column" gap="2">
                <Flex gap="2" align="center">
                  <FormField.Root size="2">
                    <FormField.Label htmlFor={`webinar-multiple-datepicker-label-${i}`} size="1">
                      Date
                    </FormField.Label>
                    <DatePicker.Root
                      config={{
                        dates: {
                          mode: 'single',
                          minDate: new Date(),
                        },
                        calendar: {
                          mode: 'fluid',
                        },
                      }}
                      value={[additionalSessions[i].date]}
                      onValueChange={(dates: Date[]) => {
                        if (dates[0].getTime() !== session.date.getTime()) {
                          const newSessions = additionalSessions.map((session, index) => {
                            if (index === i) {
                              return { ...session, date: dates[0] };
                            }
                            return session;
                          });
                          setAdditionalSessions(newSessions);
                        }
                      }}
                    >
                      <DatePicker.Trigger>
                        <DatePicker.TextField />
                      </DatePicker.Trigger>
                      <DatePicker.Content>
                        <DatePicker.Header>
                          <DatePicker.Month />
                          <DatePicker.Year />
                        </DatePicker.Header>
                        <DatePicker.Calendar>
                          <DatePicker.DaysLabels>
                            <DatePicker.DayLabel />
                          </DatePicker.DaysLabels>
                          <DatePicker.Days>
                            <DatePicker.Day />
                          </DatePicker.Days>
                        </DatePicker.Calendar>
                        <DatePicker.Actions>
                          <DatePicker.ActionReset />
                          <DatePicker.ActionApply />
                        </DatePicker.Actions>
                      </DatePicker.Content>
                    </DatePicker.Root>
                  </FormField.Root>

                  <FormField.Root size="2">
                    <FormField.Label htmlFor={`webinar-multiple-start-time-label-${i}`} size="1">
                      Start time
                    </FormField.Label>
                    <TimePicker
                      time={session.startTime}
                      setTime={(t: string) => {
                        const newSessions = additionalSessions.map((session, index) => {
                          if (index === i) {
                            return {
                              ...session,
                              startTime: t,
                            };
                          }
                          return session;
                        });
                        setAdditionalSessions(newSessions);
                      }}
                    />
                  </FormField.Root>

                  <FormField.Root size="2">
                    <FormField.Label htmlFor={`webinar-multiple-end-time-label-${i}`} size="1">
                      End time
                    </FormField.Label>
                    <TimePicker
                      time={session.endTime}
                      setTime={(t: string) => {
                        const newSessions = additionalSessions.map((session, index) => {
                          if (index === i) {
                            return {
                              ...session,
                              endTime: t,
                            };
                          }
                          return session;
                        });
                        setAdditionalSessions(newSessions);
                      }}
                    />
                  </FormField.Root>

                  <Box pt="4" px="2">
                    <IconButton
                      variant="ghost"
                      color="neutral"
                      onClick={() => {
                        const newSessions = additionalSessions.filter((_, index) => index !== i);
                        setAdditionalSessions(newSessions);
                      }}
                    >
                      <Icon icon={faTrash} size="2" label="trash" />
                    </IconButton>
                  </Box>
                </Flex>

                <Flex direction="column" gap="2">
                  <Text as="label" size="1" weight="medium">
                    Owners
                  </Text>
                  <UserAutocomplete
                    userIds={session.hostedBy}
                    onUserIdsChange={(userIds: string[]) => {
                      const newAdditionalSessions = additionalSessions.map((session, index) => {
                        if (index === i) {
                          return { ...session, hostedBy: userIds };
                        }
                        return session;
                      });
                      setAdditionalSessions(newAdditionalSessions);
                    }}
                    limitToScaliens={false}
                    limitToContributors={true}
                  />
                </Flex>

                {!_.isNil(additionalSessionsErrors[i]) && (
                  <ErrorMessage text={WebinarErrorMessages[additionalSessionsErrors[i]]} />
                )}
              </Flex>
            </Card>
          ))}

        {sessionType === RecurringType.MULTI && (
          <Button
            size="1"
            variant="ghost"
            color="accent"
            radius="small"
            onClick={() => {
              const newDate = new Date();
              const startTime = getStartTimeFromDate(newDate);
              const endTime = getEndTimeFromStartTime(startTime);
              setAdditionalSessions([
                ...additionalSessions,
                {
                  date: newDate,
                  startTime,
                  endTime,
                  hostedBy: user ? [user.id] : [],
                },
              ]);
            }}
            className="w-fit"
          >
            <Icon icon={faPlus} size="1" label="plus" />
            Add new session
          </Button>
        )}

        {sessionType === RecurringType.RECURRING &&
          additionalTimesOfDayV2.map((session: SessionTime, i: number) => (
            <Card key={`${i}-${session.date.toLocaleString()}_${session.startTime}`}>
              <Flex direction="column" gap="2">
                <Flex gap="2" align="center">
                  <FormField.Root size="2">
                    <FormField.Label htmlFor={`webinar-additional-datepicker-label-${i}`} size="1">
                      Date
                    </FormField.Label>
                    <DatePicker.Root
                      config={{
                        dates: {
                          mode: 'single',
                          minDate: new Date(),
                        },
                        calendar: {
                          mode: 'fluid',
                        },
                      }}
                      value={[session.date]}
                      onValueChange={(dates: Date[]) => {
                        if (dates[0].getTime() !== session.date.getTime()) {
                          const newAdditionalTimesOfDayV2 = additionalTimesOfDayV2.map(
                            (session, index) => {
                              if (index === i) {
                                return { ...session, date: dates[0] };
                              }
                              return session;
                            },
                          );
                          setAdditionalTimesOfDayV2(newAdditionalTimesOfDayV2);
                        }
                      }}
                    >
                      <DatePicker.Trigger>
                        <DatePicker.TextField />
                      </DatePicker.Trigger>
                      <DatePicker.Content>
                        <DatePicker.Header>
                          <DatePicker.Month />
                          <DatePicker.Year />
                        </DatePicker.Header>
                        <DatePicker.Calendar>
                          <DatePicker.DaysLabels>
                            <DatePicker.DayLabel />
                          </DatePicker.DaysLabels>
                          <DatePicker.Days>
                            <DatePicker.Day />
                          </DatePicker.Days>
                        </DatePicker.Calendar>
                        <DatePicker.Actions>
                          <DatePicker.ActionReset />
                          <DatePicker.ActionApply />
                        </DatePicker.Actions>
                      </DatePicker.Content>
                    </DatePicker.Root>
                  </FormField.Root>

                  <FormField.Root size="2">
                    <FormField.Label htmlFor={`webinar-additional-start-time-label-${i}`} size="1">
                      Start time
                    </FormField.Label>
                    <TimePicker
                      time={session.startTime}
                      setTime={(t: string) => {
                        const newAdditionalTimesOfDayV2 = additionalTimesOfDayV2.map(
                          (session, index) => {
                            if (index === i) {
                              return { ...session, startTime: t };
                            }
                            return session;
                          },
                        );
                        setAdditionalTimesOfDayV2(newAdditionalTimesOfDayV2);
                      }}
                    />
                  </FormField.Root>

                  <FormField.Root size="2">
                    <FormField.Label htmlFor={`webinar-additional-end-time-label-${i}`} size="1">
                      End time
                    </FormField.Label>
                    <TimePicker
                      time={session.endTime}
                      setTime={(t: string) => {
                        const newAdditionalTimesOfDayV2 = additionalTimesOfDayV2.map(
                          (session, index) => {
                            if (index === i) {
                              return { ...session, endTime: t };
                            }
                            return session;
                          },
                        );
                        setAdditionalTimesOfDayV2(newAdditionalTimesOfDayV2);
                      }}
                    />
                  </FormField.Root>

                  <Box pt="4">
                    <IconButton variant="ghost" color="neutral" onClick={handleRemoveTimeOfDay}>
                      <Icon icon={faTrash} size="2" label="trash" />
                    </IconButton>
                  </Box>
                </Flex>

                <Flex direction="column" gap="2">
                  <Text as="label" size="1" weight="medium">
                    Owners
                  </Text>
                  <UserAutocomplete
                    userIds={session.hostedBy}
                    onUserIdsChange={(userIds: string[]) => {
                      const newAdditionalTimesOfDayV2 = additionalTimesOfDayV2.map(
                        (session, index) => {
                          if (index === i) {
                            return { ...session, hostedBy: userIds };
                          }
                          return session;
                        },
                      );
                      setAdditionalTimesOfDayV2(newAdditionalTimesOfDayV2);
                    }}
                    limitToScaliens={false}
                    limitToContributors={true}
                  />
                </Flex>

                {!_.isNil(recurringErrors[i]) && (
                  <ErrorMessage text={WebinarErrorMessages[recurringErrors[i]]} />
                )}
              </Flex>
            </Card>
          ))}

        {sessionType === RecurringType.RECURRING && (
          <Button
            size="1"
            variant="ghost"
            color="accent"
            radius="small"
            onClick={handleAddTimeOfDay}
            className="w-fit"
          >
            <Icon icon={faPlus} size="1" label="plus" />
            Add new session
          </Button>
        )}

        {sessionType === RecurringType.RECURRING && (
          <FormField.Root size="2">
            <FormField.Label htmlFor="webinar-repeat-number-label" size="1">
              Repeat every
            </FormField.Label>
            <Flex direction="row" gap="2" align="center">
              <TextField.Root
                type="number"
                className="w-[150px]"
                id="webinar-repeat-number-text-field"
                placeholder="Enter a number"
                value={repeatNumber ?? undefined}
                onChange={e => {
                  const value = e.target.value;
                  setRepeatNumber(value === '' ? null : Number(value));
                }}
                max={7}
                min={1}
                step={1}
              />
              <Select.Root
                value={repeatType}
                onValueChange={(value: string) => setRepeatType(value as RepeatType)}
              >
                <Select.Trigger className="w-[100px]" />
                <Select.Content position="popper">
                  {Object.values(RepeatType).map(repeatType => (
                    <Select.Item key={repeatType} value={repeatType}>
                      {repeatType}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {repeatType === RepeatType.WEEKS && (
                <div className="flex gap-1">
                  {DAYS_OF_THE_WEEK.map((day: string) => (
                    <ScaleUIIcon
                      key={day}
                      variant="primary"
                      size="sm"
                      rounded
                      onClick={e =>
                        handleDaysOfTheWeekClick(
                          e as React.MouseEvent<HTMLElement, MouseEvent>,
                          day,
                        )
                      }
                      className={twMerge(
                        'bg-primary-100 hover:bg-primary-200 selected:bg-primary-300 focus:bg-primary-300',
                        daysOfTheWeek.includes(day) &&
                          'bg-primary-300 hover:bg-primary-200 selected:bg-primary-100 focus:bg-primary-100',
                      )}
                    >
                      {day[0].toUpperCase()}
                    </ScaleUIIcon>
                  ))}
                </div>
              )}
            </Flex>
          </FormField.Root>
        )}

        {sessionType === RecurringType.RECURRING && (
          <FormField.Root size="2">
            <FormField.Label htmlFor="webinar-occurrences-label" size="1">
              Total number of sessions (max 20)
            </FormField.Label>
            <TextField.Root
              type="number"
              className="w-[150px]"
              id="webinar-occurrences-text-field"
              placeholder="Enter a number"
              value={numOccurrences ?? undefined}
              onChange={e => {
                const value = e.target.value;
                setNumOccurrences(value === '' ? null : Number(value));
              }}
              max={20}
              min={1 + additionalTimesOfDayV2.length}
              step={1}
            />
          </FormField.Root>
        )}

        {sessionType === RecurringType.RECURRING && occurencesV2.length > 0 && (
          <Flex direction="column" gap="1">
            <Text as="label" size="1" weight="medium">
              Scheduled sessions
            </Text>
            <Flex direction="column" gap="1">
              {[{ date: selectedDate, startTime, endTime, hostedBy }, ...occurencesV2]
                .sort(
                  (a, b) =>
                    getNewStartDateTime(a.date, a.startTime).getTime() -
                    getNewStartDateTime(b.date, b.startTime).getTime(),
                )
                .map((o: SessionTime, i: number) => (
                  <Text
                    key={`${o.date.toLocaleString()}_${o.startTime}_${i}`}
                    size="2"
                    color="neutral"
                    weight="regular"
                    className="ml-2"
                  >
                    • {getNewStartDateTime(o.date, o.startTime).toLocaleString()}
                  </Text>
                ))}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
